import React, { useState, useRef, useEffect } from 'react'
import { CompactPicker } from 'react-color'
import styles from './FixedToolbar.module.scss'

const FONT_OPTIONS = [
  'Arial',
  'Avenir Next LT Pro',
  'AvenirNextLTPro',
  'Barcode',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
]

/**
 * FixedToolbar - A horizontal toolbar fixed at the top of the canvas panel.
 * Used in customer mode for text formatting controls.
 */
export default function FixedToolbar({
  field,
  selectedCount = 0,
  onUpdate,
  fonts = [],
  disabled = false,
}) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showFontDropdown, setShowFontDropdown] = useState(false)
  const [fontSizeInput, setFontSizeInput] = useState('')
  const [isEditingFontSize, setIsEditingFontSize] = useState(false)
  const colorPickerRef = useRef(null)
  const fontDropdownRef = useRef(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target)) {
        setShowFontDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fontOptions = fonts.length > 0
    ? fonts.map(f => ({ label: f.name, value: f.id }))
    : FONT_OPTIONS.map(f => ({ label: f, value: f }))

  const currentFontName = field?.fontName || 'Arial'
  const currentFontSize = field?.textSize || 16

  const handleFontChange = (fontName, fontId) => {
    onUpdate({ fontName, fontId })
    setShowFontDropdown(false)
  }

  const handleFontSizeChange = (delta) => {
    const newSize = Math.max(8, Math.min(200, currentFontSize + delta))
    onUpdate({ textSize: newSize })
  }

  const handleFontSizeFocus = () => {
    setIsEditingFontSize(true)
    setFontSizeInput(currentFontSize.toString())
  }

  const handleFontSizeBlur = () => {
    setIsEditingFontSize(false)
    const value = parseInt(fontSizeInput, 10)
    if (!isNaN(value) && value >= 8 && value <= 200) {
      onUpdate({ textSize: value })
    }
    setFontSizeInput('')
  }

  const handleFontSizeKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur()
    else if (e.key === 'Escape') {
      setIsEditingFontSize(false)
      setFontSizeInput('')
    }
  }

  const handleColorChange = (color) => {
    onUpdate({ color: color.hex })
  }

  const handleBoldToggle = () => {
    const currentStyle = field?.fontStyle || 'normal'
    const isBold = currentStyle.includes('bold')
    const isItalic = currentStyle.includes('italic')
    let newStyle = 'normal'
    if (!isBold && isItalic) newStyle = 'bold italic'
    else if (!isBold && !isItalic) newStyle = 'bold'
    else if (isBold && isItalic) newStyle = 'italic'
    onUpdate({ fontStyle: newStyle })
  }

  const handleItalicToggle = () => {
    const currentStyle = field?.fontStyle || 'normal'
    const isBold = currentStyle.includes('bold')
    const isItalic = currentStyle.includes('italic')
    let newStyle = 'normal'
    if (isBold && !isItalic) newStyle = 'bold italic'
    else if (!isBold && !isItalic) newStyle = 'italic'
    else if (isBold && isItalic) newStyle = 'bold'
    onUpdate({ fontStyle: newStyle })
  }

  const handleUnderlineToggle = () => {
    const currentDecoration = field?.textDecoration || ''
    if (currentDecoration.includes('underline')) {
      onUpdate({ textDecoration: currentDecoration.replace('underline', '').trim() })
    } else {
      onUpdate({ textDecoration: (currentDecoration + ' underline').trim() })
    }
  }

  const handleAlignChange = (align) => {
    onUpdate({ align })
  }

  // Prevent clicks on toolbar from propagating
  const handleToolbarClick = (e) => {
    e.stopPropagation()
  }

  return (
    <div className={styles.fixedToolbar} onClick={handleToolbarClick}>
      {/* Font selector */}
      <div className={styles.fontSelector} ref={fontDropdownRef}>
        <button
          type="button"
          className={styles.fontButton}
          onClick={() => !disabled && setShowFontDropdown(!showFontDropdown)}
          disabled={disabled}
        >
          <span className={styles.fontName}>{disabled ? 'Font' : currentFontName}</span>
          <span className={styles.dropdownArrow}>▼</span>
        </button>
        {showFontDropdown && !disabled && (
          <div className={styles.fontDropdown}>
            {fontOptions.map((font) => (
              <div
                key={font.value}
                className={styles.fontOption}
                style={{ fontFamily: font.label }}
                onClick={() => handleFontChange(font.label, font.value)}
              >
                {font.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Font size */}
      <div className={styles.fontSizeControl}>
        <button
          type="button"
          className={styles.sizeButton}
          onClick={() => handleFontSizeChange(-1)}
          disabled={disabled}
        >
          −
        </button>
        <input
          type="text"
          className={styles.sizeInput}
          value={isEditingFontSize ? fontSizeInput : (disabled ? '—' : currentFontSize)}
          onChange={(e) => setFontSizeInput(e.target.value)}
          onFocus={handleFontSizeFocus}
          onBlur={handleFontSizeBlur}
          onKeyDown={handleFontSizeKeyDown}
          disabled={disabled}
        />
        <button
          type="button"
          className={styles.sizeButton}
          onClick={() => handleFontSizeChange(1)}
          disabled={disabled}
        >
          +
        </button>
      </div>

      <div className={styles.divider} />

      {/* Bold */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (field?.fontStyle || '').includes('bold') ? styles.active : ''}`}
        title="Bold"
        onClick={handleBoldToggle}
        disabled={disabled}
      >
        <strong>B</strong>
      </button>

      {/* Italic */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (field?.fontStyle || '').includes('italic') ? styles.active : ''}`}
        title="Italic"
        onClick={handleItalicToggle}
        disabled={disabled}
      >
        <em>I</em>
      </button>

      {/* Underline */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && (field?.textDecoration || '').includes('underline') ? styles.active : ''}`}
        title="Underline"
        onClick={handleUnderlineToggle}
        disabled={disabled}
      >
        <span style={{ textDecoration: 'underline' }}>U</span>
      </button>

      <div className={styles.divider} />

      {/* Alignment */}
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.align === 'left' ? styles.active : ''}`}
        onClick={() => handleAlignChange('left')}
        title="Align left"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM2 6h8v1H2zM2 9h12v1H2zM2 12h8v1H2z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.align === 'center' ? styles.active : ''}`}
        onClick={() => handleAlignChange('center')}
        title="Align center"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM4 6h8v1H4zM2 9h12v1H2zM4 12h8v1H4z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${!disabled && field?.align === 'right' ? styles.active : ''}`}
        onClick={() => handleAlignChange('right')}
        title="Align right"
        disabled={disabled}
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM6 6h8v1H6zM2 9h12v1H2zM6 12h8v1H6z"/>
        </svg>
      </button>

      <div className={styles.divider} />

      {/* Text color */}
      <div className={styles.colorControl} ref={colorPickerRef}>
        <button
          type="button"
          className={styles.colorButton}
          onClick={() => !disabled && setShowColorPicker(!showColorPicker)}
          title="Text color"
          disabled={disabled}
        >
          <span className={styles.colorIcon}>A</span>
          <span
            className={styles.colorBar}
            style={{ backgroundColor: disabled ? '#999' : (field?.color || '#000000') }}
          />
        </button>
        {showColorPicker && !disabled && (
          <div className={styles.colorPickerPopup}>
            <CompactPicker
              color={field?.color || '#000000'}
              onChange={handleColorChange}
            />
          </div>
        )}
      </div>

      {/* Selection indicator */}
      {selectedCount > 1 && (
        <span className={styles.selectionCount}>{selectedCount} selected</span>
      )}
    </div>
  )
}
